import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { or, alias } from '@ember/object/computed';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import { queryEncode, queryDecode } from 'cio/helpers/query';
import { isEmpty, isNone } from '@ember/utils';
import {
  isSameEdge,
  retrieveOutgoingEdges,
  removeNodeFromGraph,
  addNodeToGraph,
  addNodesToGraph,
} from 'cio/utils/visual-workflow-builder/branching';
import { MAX_RANDOM_COHORT_ALLOCATION } from 'cio/utils/visual-workflow-builder';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { LAST_30_DAYS_RANGE_PRESET, defaultWorkflowMeasureValues, MEASURE_ORDER } from 'cio/utils/workflow-metrics';
import { Periods } from 'cio/utils/metrics/common';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';

export default class ActionsController extends Controller {
  queryParams = ['actionId'];
  actionId = null;

  @service account;
  @service analytics;
  @service auth;
  @service api;
  @service apiErrors;
  @service actionSubjects;
  @service features;
  @service liquidFire;
  @service visualWorkflowBuilder;
  @service environment;
  @service aggressivePositivity;
  @service store;

  selectedActionAbstract = null;

  tab = VWBTab.Build;
  metricsTabSelectedMeasures = [...defaultWorkflowMeasureValues()];
  hasChangedMeasuresAtLeastOnce = {};
  metricsTabTimePeriod = Periods.DAYS;
  metricsTabTimeRange = LAST_30_DAYS_RANGE_PRESET.getRange();

  @alias('selectedActionAbstract.isDirty') isDirty;

  showSavingStatus = true;

  highlightedNodeIds = [];

  @action
  setInitialMetricsTabMeasures() {
    // Don't modify the selected metrics if they've been changed already within
    // this session - we don't want to overwrite a customer's selection
    if (this.hasChangedMeasuresAtLeastOnce[this.environment.current.id]) {
      return;
    }

    // Find out which denominator is set for emails for metrics like
    // opened/converted etc. If it's "sent", use "sent" instead of "delivered"
    // in the default metrics tab measures.
    let denominator = findDenominator('opened', MeasureType.DeliveryMetrics, 'email_action');
    if (denominator === 'sent') {
      let measures = [...defaultWorkflowMeasureValues()].filter((m) => m !== 'delivered');
      measures.push('sent');
      this.set('metricsTabSelectedMeasures', measures);
    } else {
      this.set('metricsTabSelectedMeasures', [...defaultWorkflowMeasureValues()]);
    }
  }

  @or('addCopiedActionsAtIndexTask.isRunning', 'addActionTask.isRunning', '_addTask.isRunning', '_moveTask.isRunning')
  isSaving;

  _removeLayoutErrorForBee(action) {
    let { warnings } = action;

    if (warnings?.includes('layout')) {
      return action
        .belongsTo('template')
        .reload()
        .then((template) => {
          if (!isEmpty(template.bodyJson)) {
            return action.dismissWarningTask.perform('layout');
          }
        });
    }

    return null;
  }

  addCopiedActionsAtIndexTask = task({ drop: true }, async (dropItem, dropType, data) => {
    let placingCopiedActions = data;

    let currentWorkflowActionIds = [...this.model.workflowActionIds];
    // actions to copy are fetched and de-serialized manually, not using Ember Data for legacy reasons:
    // - it's not possible to replace environment ID in the URL with custom one in store.query(...) call
    // - action IDs are not unique between environments, and so are not safe to put into Ember Data store
    // thus, before sending them back as POST some of the data needs to be serialized again
    let serializedCopiedActions = placingCopiedActions.actions.map((a) => ({
      ...a,
      preconditions: Array.isArray(a.preconditions) ? queryEncode(a.preconditions) : a.preconditions,
      conditions: Array.isArray(a.conditions) ? queryEncode(a.conditions) : a.conditions,
    }));

    try {
      await this.addActionTask.perform(
        dropType,
        dropItem,
        {
          detail: {
            context: {
              draggable: { item: serializedCopiedActions },
            },
          },
        },
        {
          copy: true,
          copyFromEnv: placingCopiedActions.copyFromEnv,
        },
      );

      let newActionIds = [...this.model.workflowActionIds].filter((id) => !currentWorkflowActionIds.includes(id));

      if (newActionIds.length === 1) {
        this._removeLayoutErrorForBee(this.store.peekRecord('action', newActionIds[0]));
      } else {
        newActionIds.forEach((id) => {
          let action = this.store.peekRecord('action', id);
          action.get('actions').forEach((childAction) => {
            this._removeLayoutErrorForBee(childAction);
          });
          this._removeLayoutErrorForBee(action);
        });
      }
    } catch (response) {
      let { errors } = response;

      if (errors && errors[0].status === '409') {
        throw response;
      } else {
        this.apiErrors.report(response);
      }
    }
  });

  addActionTask = task({ drop: true }, async (type, item, event, meta) => {
    if (event.detail.context.draggable.action && !event.detail.context.draggable.action.isExit) {
      this.analytics.track('campaign', 'workflow_existing_item_move_completed', {
        model: this.model,
        actions: [event.detail.context.draggable.action],
        trayTab: this.tab,
        initiationType: 'keyboard',
      });
    }

    if (type === 'insertAtBranchEnd') {
      let rejoinPointId = item.action.isBranch ? this.model.branchRejoinPointsMap[item.action.id] : null;
      let result = retrieveOutgoingEdges(item.action, {
        edges: this.model.consolidatedTree.edges,
        reconnectNodeId: rejoinPointId,
      });

      if (result.outgoingEdges.length > 0) {
        if (event.detail.context.draggable.action) {
          return await this._moveTask.perform(
            event.detail.context.draggable.action,
            { insertAtEdges: result.outgoingEdges },
            null,
            meta,
          );
        } else {
          return await this._addTask.perform(
            event.detail.context.draggable?.item,
            { insertAtEdges: result.outgoingEdges },
            null,
            meta,
          );
        }
      } else if (result.leafNodes.length > 0 && event.detail.context.draggable.action) {
        let leavesToRemove = result.leafNodes.filter((leaf) => leaf.id !== event.detail.context.draggable.action.id);

        let exitsToRemove = leavesToRemove.map((leaf) => this.store.peekRecord('action', leaf.id));
        let exitEdges = leavesToRemove.map((leaf) => leaf.edge);

        return await this._moveTask.perform(
          event.detail.context.draggable.action,
          {
            insertAtEdges: result.outgoingEdges,
            branchExits: exitsToRemove,
            exitEdges,
          },
          null,
          meta,
        );
      }
    }

    if (type === 'insertAtEdge') {
      if (event.detail.context.draggable.action) {
        return await this._moveTask.perform(event.detail.context.draggable.action, { insertAtEdge: item }, null, meta);
      } else {
        return await this._addTask.perform(event.detail.context.draggable.item, { insertAtEdge: item }, null, meta);
      }
    }

    if (type === 'insertBefore') {
      if (event.detail.context.draggable.action) {
        return await this._moveTask.perform(
          event.detail.context.draggable.action,
          { insertBeforeAction: item },
          null,
          meta,
        );
      } else {
        return await this._addTask.perform(
          event.detail.context.draggable.item,
          { insertBeforeAction: item },
          null,
          meta,
        );
      }
    }

    if (type === 'insertAfter') {
      if (event.detail.context.draggable.action) {
        return await this._moveTask.perform(
          event.detail.context.draggable.action,
          { insertAfterAction: item },
          null,
          meta,
        );
      } else {
        return await this._addTask.perform(
          event.detail.context.draggable.item,
          { insertAfterAction: item },
          null,
          meta,
        );
      }
    }
  });

  _moveTask = task(
    async (movedAction, { insertBeforeAction, insertAfterAction, insertAtEdge, insertAtEdges, branchExits }) => {
      if (movedAction.isExit) {
        let edgesIncomingToExit = [];
        let remainingEdges = [];

        this.model.consolidatedTree.edges.forEach((edge) => {
          if (edge.to === movedAction.id) {
            edgesIncomingToExit.push(edge);
          } else {
            remainingEdges.push(edge);
          }
        });

        let newEdges = [...remainingEdges];

        if (insertAfterAction) {
          let edgeLeavingInsertAfterAction = this.model.consolidatedTree.edges.find(
            (edge) => edge.from === insertAfterAction.id,
          );
          insertAtEdges = [edgeLeavingInsertAfterAction];
        } else if (insertBeforeAction) {
          let edgesIncomingToInsertBeforeAction = this.model.consolidatedTree.edges.filter(
            (edge) => edge.to === insertBeforeAction.id,
          );
          insertAtEdges = edgesIncomingToInsertBeforeAction;
        } else if (insertAtEdge) {
          insertAtEdges = [insertAtEdge];
        } else if (branchExits) {
          insertAtEdges = this.model.consolidatedTree.edges.filter((edge) =>
            branchExits.some((exit) => exit.id === edge.to),
          );
        }

        if (branchExits && branchExits.length > 0) {
          newEdges = newEdges.filter((edge) => !insertAtEdges.some((e) => isSameEdge(e, edge)));

          let touchedActionIds = [];

          insertAtEdges.forEach((edge) => {
            newEdges.push({ ...edge, to: movedAction.id });
            touchedActionIds.push(edge.from);
          });

          let exitsToBeDeleted = branchExits.map((exit) => exit.id);
          newEdges.push(...edgesIncomingToExit);

          try {
            this.set('selectedActionAbstract', null);
            await this.model.deleteActions({ actionIds: exitsToBeDeleted, edges: newEdges, touchedActionIds });
          } finally {
            this.model.unloadActions(exitsToBeDeleted);
            this.model.parseTree();
          }
          return;
        } else {
          let target = insertAtEdges[0].to;
          let touchedActionIds = [];

          edgesIncomingToExit.forEach((edge) => {
            newEdges.push({ ...edge, to: target });
            touchedActionIds.push(edge.from);
          });

          let exitsToBeDeleted = [movedAction.id];

          try {
            this.set('selectedActionAbstract', null);
            await this.model.deleteActions({ actionIds: exitsToBeDeleted, edges: newEdges, touchedActionIds });
          } finally {
            this.model.unloadActions(exitsToBeDeleted);
            this.model.parseTree();
          }
          return;
        }
      }

      if (insertAfterAction || insertBeforeAction) {
        insertAtEdges = [];
      } else if (insertAtEdge || insertAtEdges) {
        if (insertAtEdge) {
          insertAtEdges = [insertAtEdge];
        }
      }

      let rejoinPointId = movedAction.isBranch ? this.model.branchRejoinPointsMap[movedAction.id] : null;
      let result = removeNodeFromGraph(movedAction, this.model.consolidatedTree.edges, insertAtEdges, rejoinPointId);

      let meta = {
        newEdges: result.edges,
      };

      if (movedAction.isBranch) {
        meta.branchOutgoingEdges = result.edgesLeavingNode;
        meta.branchInternalEdges = result.edgesWithinNode;
      }

      return await this._addTask.perform(
        null,
        {
          insertBeforeAction,
          insertAfterAction,
          insertAtEdges: result.edgesToUpdate.length > 0 ? result.edgesToUpdate : null,
        },
        movedAction,
        meta,
      );
    },
  );

  _addTask = task(
    async (
      actionItem,
      { insertBeforeAction, insertAfterAction, insertAtEdge, insertAtEdges },
      existingAction,
      { branchOutgoingEdges, branchInternalEdges, copyFromEnv, copy = false, newEdges = [] } = {},
    ) => {
      let newAction;

      let touchedActionIds = [];

      if (!copy) {
        if (!existingAction) {
          if (
            this.auth.user?.userFlags.aggressive_positivity_available &&
            this.auth.user?.userFlags.aggressive_positivity_mode_on
          ) {
            this.aggressivePositivity.playRandom();
          }

          let attributes = { ...actionItem.attributes };

          if (actionItem.info.nameRequired) {
            let actionsOfSameTypeAlreadyPresent = this.model.consolidatedActions.filterBy(
              'type',
              actionItem.type,
            ).length;
            attributes.name = `${actionItem.info.newActionName} ${actionsOfSameTypeAlreadyPresent + 1}`;
          } else if (actionItem.info.nameOptional) {
            attributes.name = '';
          }

          attributes.id = -1;
          // attributes.id = UUID.generate();
          attributes.campaign = this.model;
          newAction = this.store.createRecord('action', attributes);
        } else {
          newAction = existingAction;
          touchedActionIds.push(newAction.id);
        }
      } else {
        // Copying an item will give us a serialized action, where
        // "multiConditions" is serialized into "multi_conditions". However, the
        // steps below (like addNodeToGraph) need deserialized "multiConditions"
        // to perform the graph operations, so we need to manually copy those
        // values over.
        actionItem.forEach((item) => {
          if (item.type === 'multi_split_branch_action' && item.multi_conditions) {
            item.multiConditions = item.multi_conditions.map((condition) => queryDecode(condition));
          }
        });

        // We don't want to count sub-actions as normal actions. When copying them
        // we will send their IDs as 0, but the other actions will have their IDs
        // indexed.
        //
        // So we break it down into two arrays and treat them separately.

        let childActions = actionItem.filter((item) => {
          // A child action must have a split parent item that is also being dragged.
          // If a child item's parent is not being dragged, the child is the parent.
          return item.splitParentId && actionItem.map((item) => item.id).includes(item.splitParentId);
        });

        let parentActions = actionItem.filter((item) => !childActions.includes(item));
        let actionsLength = parentActions.length;
        let idMap = {};

        // For each child action, set their ID to 0.
        childActions.forEach((item) => (item.id = '0'));

        // For each parent action, set an indexed ID. Since we're only looping
        // over parent actions now, the index is correct by default.
        parentActions.forEach((item, index) => {
          let oldId = item.id;
          item.id = `${index - actionsLength}`;
          idMap[oldId] = item.id;

          // Since the child actions will have an ID of 0 now, we can simply set
          // action_ids to the same array mapped to 0.
          if (item.type === 'split_randomized_action') {
            item.action_ids = item.action_ids.map((_) => '0');
          }
        });

        // Set the correct parent ID for each child ID
        childActions.forEach((item) => {
          item.split_parent_id = idMap[item.splitParentId];
          delete item.splitParentId;
        });
      }

      if (newAction && newAction.type === 'random_cohort_branch_action' && isEmpty(newAction.cohorts)) {
        newAction.set('cohorts', [MAX_RANDOM_COHORT_ALLOCATION, 0]);
      }

      if (newAction && newAction.type === 'multi_split_branch_action' && isEmpty(newAction.multiConditions)) {
        newAction.set('multiConditions', [[], []]);
      }

      if (newEdges.length === 0) {
        newEdges = [...this.model.consolidatedTree.edges];
      }

      if (insertBeforeAction || insertAfterAction || insertAtEdges || insertAtEdge) {
        if (insertBeforeAction) {
          let edgesIncomingToInsertBeforeAction = newEdges.filter((edge) => edge.to === insertBeforeAction.id);
          insertAtEdges = edgesIncomingToInsertBeforeAction;

          if (insertAtEdges.length === 0) {
            // If this is a new event triggered campaign (with no
            // filter_match_delay action), there will be no existing edges yet.
            // Add a fake edge so that "addNodeToGraph" has a valid target to use.
            insertAtEdges = [{ to: insertBeforeAction.id }];
          }
        } else if (insertAfterAction) {
          let edgeLeavingInsertAfterAction = newEdges.find((edge) => edge.from === insertAfterAction.id);
          insertAtEdges = [edgeLeavingInsertAfterAction];
        } else if (insertAtEdge) {
          insertAtEdges = [insertAtEdge];
        }

        insertAtEdges.forEach((edge) => touchedActionIds.push(edge.from));

        if (copy) {
          let splitParentIds = actionItem.filter((item) => item.type === 'split_randomized_action').mapBy('id');
          let nonVariationActions = actionItem.filter(
            (item) => isNone(item.split_parent_id) || !splitParentIds.includes(item.split_parent_id),
          );
          newEdges = addNodesToGraph({
            nodes: nonVariationActions,
            edges: newEdges,
            edgesToInsertAt: insertAtEdges,
            existing: true,
          });
        } else {
          let edges = [...newEdges];
          if (branchInternalEdges) {
            edges.push(...branchInternalEdges);
          }
          let meta = {
            node: newAction,
            edgesToInsertAt: insertAtEdges,
            edges,
          };

          if (branchOutgoingEdges) {
            meta.outgoingEdges = branchOutgoingEdges;
          }
          newEdges = addNodeToGraph(meta);
        }
      }

      if (!existingAction) {
        // creating action
        try {
          let data = { edges: newEdges };

          if (copy) {
            data.actions = actionItem;
            data.copyFromEnv = copyFromEnv;
          } else {
            data.actions = [newAction.serialize({ includeId: true })];
            newAction.unloadRecord();
          }

          try {
            await this.model.addActions(data);
          } catch (err) {
            this.apiErrors.report(err);
          }

          // Remove the temporary record
        } finally {
          // this.model.set('workflowActionIds', [...this.model.workflowActionIds, newAction.id]);
          this.model.parseTree();
        }
      } else {
        // moving action
        try {
          await this.model.moveAction({
            edges: newEdges,
            touchedActionIds,
          });
        } finally {
          this.model.parseTree();
        }
      }
    },
  );

  @action
  actionDestroyed() {
    this.get('model').reload();
    this.get('actionSubjects.fetch').perform(this.get('model.id'));
  }

  @action
  saveCampaign() {
    return this.saveCampaignTask.perform();
  }

  @action
  reset() {
    return this.resetTask.perform();
  }

  @action
  selectMetricsTabMeasure(measure) {
    this.set(
      'metricsTabSelectedMeasures',
      [...this.metricsTabSelectedMeasures, measure].sort((a, b) => MEASURE_ORDER.indexOf(a) - MEASURE_ORDER.indexOf(b)),
    );

    this.set('hasChangedMeasuresAtLeastOnce', {
      ...this.hasChangedMeasuresAtLeastOnce,
      [this.environment.current.id]: true,
    });
  }

  @action
  deselectMetricsTabMeasure(measure) {
    this.set(
      'metricsTabSelectedMeasures',
      this.metricsTabSelectedMeasures
        .filter((m) => m !== measure)
        .sort((a, b) => MEASURE_ORDER.indexOf(a) - MEASURE_ORDER.indexOf(b)),
    );

    this.set('hasChangedMeasuresAtLeastOnce', {
      ...this.hasChangedMeasuresAtLeastOnce,
      [this.environment.current.id]: true,
    });
  }

  @action
  changeMetricsTabTimePeriod(timePeriod) {
    this.set('metricsTabTimePeriod', timePeriod);
  }

  @action
  changeMetricsTabTimeRange(timeRange) {
    this.set('metricsTabTimeRange', timeRange);
  }
}
